// react
import { createSlice, PayloadAction } from "@reduxjs/toolkit";
// redux
import { createAppAsyncThunk } from "../redux/hooks";
// api
import { createShellClient } from "../api/clientFactory";
import * as Client from "../api/Client";
import { call } from "../api/callHelper";
// common
import { logError } from "../common/LogHelper";

// state
export interface IShellState {
    busyCount: number;
    dirtyCount: number;
    pageTitle: string | undefined;
    showPageHeader: boolean | undefined;
    includePadding: boolean | undefined;
    isStaticConfigurationLoaded: boolean | undefined;
    featureSwitches: Client.NeatsFeatureSwitchEnum[];
    portalLinks: IShellPortalLink[];
    userToken: string | undefined;
    helpUrl: string | undefined;
}

interface IShellPortalLink {
    name: Client.ExternalLinkEnum;
    url: string;
}

// initial state
const initialState: IShellState = {
    busyCount: 0,
    dirtyCount: 0,
    pageTitle: "",
    showPageHeader: true,
    includePadding: true,
    isStaticConfigurationLoaded: false,
    featureSwitches: [],
    portalLinks: [],
    userToken: undefined,
    helpUrl: undefined,
};

// shared reducer logic
const incrementBusy = (state: IShellState) => {
    state.busyCount += 1;
};
const decrementBusy = (state: IShellState) => {
    if (state.busyCount > 0) {
        state.busyCount -= 1;
    } else {
        state.busyCount = 0;
    }
};

// reducer
export const shellSlice = createSlice({
    name: "shell",
    // `createSlice` will infer the state type from the `initialState` argument
    initialState,
    reducers: {
        setBusy: incrementBusy,
        clearBusy: decrementBusy,
        setDirty: (state) => {
            state.dirtyCount += 1;
        },
        clearDirty: (state) => {
            if (state.dirtyCount > 0) {
                state.dirtyCount -= 1;
            } else {
                state.dirtyCount = 0;
            }
        },
        setLayout: {
            reducer(state, action: PayloadAction<{ pageTitle: string; showPageHeader: boolean; includePadding: boolean; helpUrl: string | undefined }>) {
                state.pageTitle = action.payload.pageTitle;
                state.showPageHeader = action.payload.showPageHeader;
                state.includePadding = action.payload.includePadding;
                state.helpUrl = action.payload.helpUrl;
            },
            prepare(pageTitle: string, showPageHeader: boolean | undefined = true, includePadding: boolean | undefined = true, helpUrl: string | undefined = undefined) {
                return {
                    payload: { pageTitle, showPageHeader, includePadding, helpUrl },
                };
            },
        },
        userTokenLoaded: (state, action: PayloadAction<string>) => {
            state.userToken = action.payload;
        },
    },
    extraReducers: (builder) => {
        builder
            .addCase(loadStaticConfiguration.pending, (state) => {
                incrementBusy(state);
                state.featureSwitches = [];
                state.portalLinks = [];
            })
            .addCase(loadStaticConfiguration.fulfilled, (state, action) => {
                if (action.payload) {
                    state.featureSwitches = action.payload.featureSwitches;
                    state.portalLinks = action.payload.portalLinks;
                    state.isStaticConfigurationLoaded = true;
                }
                decrementBusy(state);
            });
    },
    selectors: {
        selectIsBusy: (state) => state.busyCount > 0,
        selectIsDirty: (state) => state.dirtyCount > 0,
        selectPageTitle: (state) => state.pageTitle,
        selectShowPageHeader: (state) => state.showPageHeader,
        selectIncludePadding: (state) => state.includePadding,
        selectHelpUrl: (state) => state.helpUrl,
        selectUserToken: (state) => state.userToken,
        selectPortalLinks: (state) => state.portalLinks,
        selectFeatureSwitches: (state) => state.featureSwitches,
        selectIsStaticConfigurationLoaded: (state) => state.isStaticConfigurationLoaded,
    },
});

// actions
export const { setBusy, clearBusy, setDirty, clearDirty, setLayout, userTokenLoaded } = shellSlice.actions;
// selectors
export const {
    selectIsBusy,
    selectIsDirty,
    selectPageTitle,
    selectShowPageHeader,
    selectIncludePadding,
    selectHelpUrl,
    selectUserToken,
    selectPortalLinks,
    selectFeatureSwitches,
    selectIsStaticConfigurationLoaded,
} = shellSlice.selectors;

// TODO need to decide what we want to retry and log here
// previously we were using simpleCall which retried, logged to appInsights and handled API error toast notifications
// now there is only call which retries, logs to appInsights and returns a CallResultError
// - do we need to log anything here, and do we need to retry these calls?
// thunks
export const loadStaticConfiguration = createAppAsyncThunk("shell/loadStaticConfiguration", async () => {
    const client = createShellClient();

    const featureSwitchesRequest = new Client.GetPublicShellFeatureSwitchesRequestDto();
    const featureSwitchesResult = await call(() => client.getPublicShellFeatureSwitches(featureSwitchesRequest));
    if (featureSwitchesResult.Error) {
        logError(`shell/loadStaticConfiguration/getPublicShellFeatureSwitches failed with correlation '${featureSwitchesResult.Error.unitOfWorkCorrelationId}'`);
        return;
    }
    const portalLinksRequest = new Client.GetPublicShellPortalLinksRequestDto();
    const portalLinksResult = await call(() => client.getPublicShellPortalLinks(portalLinksRequest));
    if (portalLinksResult.Error) {
        logError(`shell/loadStaticConfiguration/getPublicShellPortalLinks failed with correlation '${portalLinksResult.Error.unitOfWorkCorrelationId}'`);
        return;
    }

    const featureSwitches = featureSwitchesResult.Response?.featureSwitches ?? [];
    const portalLinks: IShellPortalLink[] = portalLinksResult.Response?.links.map((l) => ({ name: l.name, url: l.url })) ?? [];

    return { featureSwitches, portalLinks };
});

export default shellSlice.reducer;
